// ══════════════════════════════════════════════════════════════════════════════
//  src/components/business_landing/BDashboard.jsx
//  «DASHBOARD GERENCIAL EN TIEMPO REAL» — lo que el portal de empresa mide de verdad
//
//  Rob (14-09-2026): la sección estaba desactualizada. Prometía «las métricas que
//  tu empresa solicita», una visualización «cada 7 días», satisfacción, nivel
//  promedio y un «top 5 de empleados» que el portal no calcula.
//
//  Ahora enseña, con DATOS DE EJEMPLO dichos como tales, las mismas piezas que ve
//  una empresa en su portal (`/empresa/<clave>`, EMPRESAS.md §4.6 y Fase 6).
//  Los números del ejemplo cuadran entre sí con las reglas reales de `metricas.py`
//  (asistencia sin las faltas justificadas, puntualidad, ritmo contra el plan).
//  Si el portal cambia lo que mide, esta sección tiene que cambiar con él.
// ══════════════════════════════════════════════════════════════════════════════

import './BDashboard.css';

// Asistencia del ejemplo: 66 presentes de 72 (las 4 justificadas no cuentan) = 92 %.
const ASISTENCIA = [
  ['ok', 'Asistió a tiempo', 58],
  ['inc', 'Llegó tarde o salió antes', 8],
  ['just', 'Faltó con justificante', 4],
  ['sin', 'Faltó sin aviso', 6],
];
const TOTAL_ASISTENCIA = ASISTENCIA.reduce((s, [, , n]) => s + n, 0);

const KPIS = [
  ['Participación', '92 %', '22 de 24 practicaron en los últimos 30 días'],
  ['Asistencia a clase', '92 %', 'Las faltas con justificante no cuentan en contra'],
  ['Puntualidad', '88 %', 'Llegar a tiempo y quedarse toda la clase'],
  ['Ritmo del curso', '96 %', 'Avance del curso contra lo planeado a hoy'],
];

const GRUPOS = [
  { nombre: 'Dublín', plan: 'A1 → A2', avance: 46, esperado: 50 },
  { nombre: 'Berlín', plan: 'B1 → B2', avance: 38, esperado: 33 },
  { nombre: 'Lisboa', plan: 'C1 → C2', avance: 71, esperado: 75 },
];

const ALERTAS = [
  'Luis Ortega lleva 9 días sin practicar.',
  'Karla Méndez faltó a sus últimas 2 clases sin aviso.',
  'Pedro Salas todavía no ha entrado a la plataforma.',
];

const LO_QUE_MIDE = [
  ['🗓️', 'Asistencia a cada clase', 'Asistió, llegó tarde, salió antes o faltó. Si la falta fue justificada —médica o por carga de trabajo— se ve el motivo y no cuenta en contra.'],
  ['🎓', 'La evaluación del profesor', 'Siete criterios en cada clase: gramática, vocabulario, pronunciación, fluidez, interacción, comprensión y atención. Con un comentario escrito para la empresa.'],
  ['📈', 'El curso contra el plan', 'Cada grupo tiene su recorrido y su periodo (por ejemplo, de A1 a B1 en tres meses). Ves cuánto lleva contra lo que debería llevar hoy.'],
  ['👤', 'La ficha de cada empleado', 'Su grupo y su profesor, su nivel, días activos, horas de práctica, minutos hablando con la IA y la última vez que practicó.'],
  ['🔔', 'Alertas a tiempo', 'Quién nunca ha entrado, quién dejó de practicar y quién suma faltas seguidas sin aviso.'],
  ['💰', 'El retorno de tu inversión', 'Costo por empleado activo, por clase asistida y por hora de práctica. Comparado contra el periodo anterior y entre grupos.'],
  ['🎯', 'Metas acordadas contigo', 'Metas a corto, mediano y largo plazo, fijadas antes de cada renovación, con su avance.'],
  ['⬇️', 'Excel y PDF', 'Descarga el informe del periodo que elijas: resumen, grupos, empleados y clases.'],
  ['🔒', 'Privacidad del empleado', 'Nunca ves las conversaciones de tus empleados con la IA ni sus CV del simulador de entrevistas.'],
];

const pct = (n) => `${Math.round((n / TOTAL_ASISTENCIA) * 100)} %`;

function BDashboard({ id }) {
  return (
    <section className="b-dashboard-section" id={id || 'dashboard'} data-prueba="b-dashboard">
      <div className="container">
        <h2 className="section-title">
          <span className="highlight-blue">Dashboard Gerencial</span> en tiempo real
        </h2>
        <p className="section-subtitle">
          Tu empresa entra a su propio portal con un enlace privado y una contraseña. Se actualiza solo, cada minuto,
          mientras lo tienes abierto: asistencia real a las clases, el avance del curso contra el plan de cada grupo
          y el retorno de tu inversión.
        </p>

        <figure className="bd-vitrina" aria-label="Ejemplo del portal de empresa con datos ficticios">
          <figcaption className="bd-aviso-ejemplo">Ejemplo con datos ficticios · así se ve el portal de tu empresa</figcaption>

          <div className="bd-cabecera">
            <div>
              <div className="bd-empresa">Tu empresa</div>
              <div className="bd-sub">24 empleados en el programa · actualizado hace un momento</div>
            </div>
            <div className="bd-chips" aria-hidden="true">
              <span className="bd-chip bd-chip--activo">✓ Todo el contrato</span>
              <span className="bd-chip">90 días</span>
              <span className="bd-chip">30 días</span>
              <span className="bd-chip">7 días</span>
            </div>
          </div>

          <div className="bd-tarjeta bd-indice">
            <div>
              <div className="bd-etiqueta">Aprovechamiento del programa</div>
              <div className="bd-hero">92<small>de 100</small></div>
              <span className="bd-semaforo"><span className="bd-semaforo-punto" aria-hidden="true">✓</span>En buen camino</span>
            </div>
            <div className="bd-ejes">
              <Medidor etiqueta="Con su profesor (clase)" valor={89} nota="Asistencia, puntualidad y la evaluación del profesor" />
              <Medidor etiqueta="En la plataforma (curso)" valor={96} nota="Cuánto avanza el curso contra el tiempo planeado" />
            </div>
          </div>

          <div className="bd-kpis">
            {KPIS.map(([etiqueta, valor, nota]) => (
              <div key={etiqueta} className="bd-tarjeta">
                <div className="bd-etiqueta">{etiqueta}</div>
                <div className="bd-kpi">{valor}</div>
                <div className="bd-nota">{nota}</div>
              </div>
            ))}
          </div>

          <div className="bd-rejilla">
            <div className="bd-tarjeta">
              <div className="bd-titulo">Asistencia a clases</div>
              <div className="bd-pila" role="img"
                aria-label={`Asistencia: ${ASISTENCIA.map(([, t, n]) => `${t} ${n}`).join(', ')}`}>
                {ASISTENCIA.map(([k, t, n]) => (
                  <span key={k} className={`bd-seg bd-seg--${k}`} style={{ flex: `${n} 1 0%` }} title={`${t}: ${n} (${pct(n)})`} />
                ))}
              </div>
              <div className="bd-leyenda">
                {ASISTENCIA.map(([k, t, n]) => (
                  <span key={k} className="bd-leyenda-item">
                    <span className={`bd-muestra bd-seg--${k}`} aria-hidden="true" />{t}: <b>{n}</b>
                  </span>
                ))}
              </div>
            </div>

            <div className="bd-tarjeta">
              <div className="bd-titulo">Alertas</div>
              <ul className="bd-alertas">
                {ALERTAS.map((a) => <li key={a}><span aria-hidden="true">⚠️</span>{a}</li>)}
              </ul>
            </div>
          </div>

          <div className="bd-tarjeta">
            <div className="bd-titulo">Avance del curso contra lo planeado</div>
            <div className="bd-barras">
              {GRUPOS.map((g) => (
                <div key={g.nombre} className="bd-barra-fila"
                  title={`${g.nombre}: lleva ${g.avance} % de su recorrido; lo esperado a hoy es ${g.esperado} %`}>
                  <span className="bd-barra-etiqueta">{g.nombre} · <b>{g.plan}</b></span>
                  <span className="bd-barra-pista" role="img"
                    aria-label={`${g.nombre}, ${g.plan}: lleva ${g.avance} %, esperado a hoy ${g.esperado} %`}>
                    <span className="bd-barra" style={{ width: `${g.avance}%` }} />
                    <span className="bd-barra-marca" style={{ left: `${g.esperado}%` }} />
                  </span>
                  <span className="bd-barra-valor">{g.avance} %</span>
                </div>
              ))}
            </div>
            <div className="bd-leyenda">
              <span className="bd-leyenda-item"><span className="bd-muestra bd-seg--ok" aria-hidden="true" />Avance del recorrido</span>
              <span className="bd-leyenda-item"><span className="bd-muestra bd-muestra--marca" aria-hidden="true" />Esperado a hoy</span>
            </div>
          </div>
        </figure>

        <h3 className="bd-subtitulo">Lo que mide tu portal</h3>
        <ul className="bd-mide" data-prueba="lo-que-mide">
          {LO_QUE_MIDE.map(([icono, titulo, texto]) => (
            <li key={titulo} className="bd-mide-item">
              <span className="bd-mide-icono" aria-hidden="true">{icono}</span>
              <div>
                <h4>{titulo}</h4>
                <p>{texto}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Medidor({ etiqueta, valor, nota }) {
  return (
    <div>
      <div className="bd-medidor-cab"><span>{etiqueta}</span><b>{valor} %</b></div>
      <div className="bd-medidor" role="img" aria-label={`${etiqueta}: ${valor} %`}>
        <span className="bd-medidor-relleno" style={{ width: `${valor}%` }} />
      </div>
      <div className="bd-nota">{nota}</div>
    </div>
  );
}

export default BDashboard;
